import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Switch,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Sync as SyncIcon,
  Edit as EditIcon,
  SportsFootball as FootballIcon,
  SportsBasketball as BasketballIcon,
} from '@mui/icons-material';
import { adminSportsApi } from '../../api/sports';
import type { SportConfig } from '../../types';
import { useDocumentTitle } from '../../hooks/core';

const sportIcons: Record<string, React.ReactNode> = {
  nfl: <FootballIcon />,
  ncaaf: <FootballIcon />,
  nba: <BasketballIcon />,
  ncaab: <BasketballIcon />,
  wnba: <BasketballIcon />,
};

export function SportsConfigPage() {
  useDocumentTitle('Live Sports Config');

  const [configs, setConfigs] = useState<SportConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncing, setSyncing] = useState<string | null>(null);

  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [editConfig, setEditConfig] = useState<SportConfig | null>(null);
  const [editSeasonYear, setEditSeasonYear] = useState(2026);
  const [editQuarters, setEditQuarters] = useState(4);
  const [editLiveBoardCreditCost, setEditLiveBoardCreditCost] = useState(1);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const data = await adminSportsApi.getAllSportConfigs();
      setConfigs(data);
      setError(null);
    } catch {
      setError('Failed to load sport configurations');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (config: SportConfig) => {
    try {
      await adminSportsApi.updateSportConfig(config.sportKey, { enabled: !config.enabled });
      setSuccess(`${config.displayName} ${!config.enabled ? 'enabled' : 'disabled'}`);
      fetchData();
    } catch {
      setError(`Failed to update ${config.displayName}`);
    }
  };

  const handleSync = async (sportKey: string) => {
    try {
      setSyncing(sportKey);
      const result = await adminSportsApi.syncGames(sportKey);
      setSuccess(`Synced ${result.gamesSynced} games and ${result.teamsSynced} teams`);
      if (result.teamsError) {
        setError(result.teamsError);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Sync failed';
      setError(message);
    } finally {
      setSyncing(null);
    }
  };

  const handleEditOpen = (config: SportConfig) => {
    setEditConfig(config);
    setEditSeasonYear(config.seasonYear);
    setEditQuarters(config.quarters);
    setEditLiveBoardCreditCost(config.liveBoardCreditCost || 1);
    setEditError(null);
    setEditDialogOpen(true);
  };

  const handleEditClose = () => {
    setEditDialogOpen(false);
    setEditConfig(null);
    setEditError(null);
  };

  const handleEditSave = async () => {
    if (!editConfig) return;
    try {
      setEditLoading(true);
      setEditError(null);
      await adminSportsApi.updateSportConfig(editConfig.sportKey, {
        seasonYear: editSeasonYear,
        quarters: editQuarters,
        liveBoardCreditCost: editLiveBoardCreditCost,
      });
      setSuccess(`${editConfig.displayName} updated`);
      handleEditClose();
      fetchData();
    } catch (err: unknown) {
      setEditError(err instanceof Error ? err.message : 'Failed to update');
    } finally {
      setEditLoading(false);
    }
  };

  // Clear success message after 3 seconds
  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" component="h1" fontWeight="bold">
            Live Sports Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage sports API integrations, sync schedules, and enable/disable sports
          </Typography>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      <Paper sx={{ mb: 3 }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Sport</TableCell>
                <TableCell>Key</TableCell>
                <TableCell align="center">Enabled</TableCell>
                <TableCell align="center">Season</TableCell>
                <TableCell align="center">Quarters</TableCell>
                <TableCell align="center">Live Board Credits</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {configs.map((config) => (
                <TableRow key={config.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {sportIcons[config.sportKey] || <FootballIcon color="action" />}
                      <Typography fontWeight="medium">{config.displayName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={config.sportKey}
                      size="small"
                      variant="outlined"
                      sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Switch
                      checked={config.enabled}
                      onChange={() => handleToggle(config)}
                      size="small"
                      color="success"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{config.seasonYear}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2">{config.quarters}</Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight="bold" color="primary.main">
                      {config.liveBoardCreditCost || 1}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                      <Tooltip title="Edit season & quarters">
                        <IconButton size="small" onClick={() => handleEditOpen(config)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Sync games & teams from API">
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={syncing === config.sportKey ? <CircularProgress size={14} /> : <SyncIcon />}
                            onClick={() => handleSync(config.sportKey)}
                            disabled={!config.enabled || syncing !== null}
                            sx={{ textTransform: 'none', fontSize: '0.75rem' }}
                          >
                            Sync
                          </Button>
                        </span>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
        <Typography variant="subtitle2" gutterBottom color="text.secondary">
          How Live Sports Integration Works
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Enable sports to allow users to browse real game schedules when creating boards.
          Use the Sync button to pull the latest schedule and team data from SportsData.io.
        </Typography>
        <Box component="ul" sx={{ mt: 1, pl: 2 }}>
          <li>
            <Typography variant="body2" color="text.secondary">
              <strong>API Keys:</strong> Set <code>SPORTSDATA_API_KEY_NFL</code> (etc.) in your environment
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              <strong>Sync:</strong> Pulls game schedules and team colors/logos from the API
            </Typography>
          </li>
          <li>
            <Typography variant="body2" color="text.secondary">
              <strong>Enable/Disable:</strong> Controls which sports appear in the board creation flow
            </Typography>
          </li>
        </Box>
      </Paper>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleEditClose} maxWidth="xs" fullWidth>
        <DialogTitle>
          Edit {editConfig?.displayName}
        </DialogTitle>
        <DialogContent>
          {editError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {editError}
            </Alert>
          )}
          <TextField
            autoFocus
            margin="dense"
            label="Season Year"
            type="number"
            fullWidth
            value={editSeasonYear}
            onChange={(e) => setEditSeasonYear(parseInt(e.target.value) || 2026)}
            inputProps={{ min: 2000, max: 2100 }}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Default Quarters"
            type="number"
            fullWidth
            value={editQuarters}
            onChange={(e) => setEditQuarters(Math.min(4, Math.max(2, parseInt(e.target.value) || 4)))}
            inputProps={{ min: 2, max: 4 }}
            helperText="Number of scoring periods (2 for soccer, 3 for hockey, 4 for football/basketball)"
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Live Board Credit Cost"
            type="number"
            fullWidth
            value={editLiveBoardCreditCost}
            onChange={(e) => setEditLiveBoardCreditCost(Math.max(1, parseInt(e.target.value) || 1))}
            inputProps={{ min: 1, max: 100 }}
            helperText="Credits required to create a board linked to a live game for this sport"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditClose} disabled={editLoading}>
            Cancel
          </Button>
          <Button onClick={handleEditSave} variant="contained" disabled={editLoading}>
            {editLoading ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default SportsConfigPage;
